import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import { makeOfflineRequest } from "../../shared/utils.js/offlineRequestManager";

// get Employees
export const getEmployees = createAsyncThunk(
  "Employee/getEmployees",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      // Use provided language or fallback to current language
      const currentLang = params.language || getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const response = await axios.get(
        `${BASE_URL}/employees?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get employees!";
      return rejectWithValue(message);
    }
  },
);

// Get employee details
export const getEmployeeDetails = createAsyncThunk(
  "Employee/getEmployeeDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/employees/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get employee details!";
      return rejectWithValue(message);
    }
  },
);

// Update employee
export const updateEmployee = createAsyncThunk(
  "Employee/updateEmployee",
  async ({ id, employeeData, newIsActive }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/employees/${id}`,
        employeeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            // Don't set Content-Type for FormData - let browser set it automatically
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update employee!";
      return rejectWithValue(message);
    }
  },
);

// Add employee
export const addEmployee = createAsyncThunk(
  "Employee/addEmployee",
  async (employeeData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/employees`, employeeData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          // Don't set Content-Type for FormData - let browser set it automatically
        },
      });
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add employee!";
      return rejectWithValue(message);
    }
  },
);

// Delete employee
export const deleteEmployee = createAsyncThunk(
  "Employee/deleteEmployee",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/employees/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete employee!";
      return rejectWithValue(message);
    }
  },
);

// Toggle Employee Status
export const toggleEmployeeStatus = createAsyncThunk(
  "Employee/toggleEmployeeStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.patch(
        `${BASE_URL}/employees/${id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle employee status!";
      return rejectWithValue(message);
    }
  },
);
